import { useState } from 'react';

import { MultipleOfferAvailability } from '../../../../types/MultipleOfferAvailability';
import { RequestForQuoteGatewayMessage } from '../../../../types/RequestForQuoteGatewayMessage';
import { DropdownMenu } from '../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { CancelPopup } from '../../../common/CancelPopup/CancelPopup';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconCancel } from '../../../common/icons/cdl/Cancel';
import { IconDots } from '../../../common/icons/cdl/Dots';
import { IconDownload } from '../../../common/icons/cdl/Download';
import { IconExport } from '../../../common/icons/cdl/Export';
import { IconRestore } from '../../../common/icons/cdl/Restore';
import { ShortcutMenuContextAction } from '../../../common/ShortcutMenu/components/ShortcutMenuContext';
import { useRegisterShortcutMenuContextActions } from '../../../common/ShortcutMenu/hooks/useRegisterShortcutMenuContextActions';
import { SimpleConfirmPopup } from '../../../offer/detail/common/components/SimpleConfirmPopup';
import { isAdminReopenableOffer } from '../../../offer/detail/common/helpers/isAdminReopenableOffer';
import { useOfferReopen } from '../../../offer/detail/common/hooks/useOfferReopen';
import { InitialEnquiryPopup } from '../../../offer/detail/InitialEnquiryPopup';
import { OfferState } from '../../../offer/model/OfferState';
import { createRfqExportRequest } from '../../helpers/createRfqExportRequest';
import { useRfqDecline } from '../../hooks/useRfqDecline';
import { useRfqExport } from '../../hooks/useRfqExport';
import { getOpenRfqOffers } from '../helpers/getOpenRfqOffers';
import { getRfqCancelActionLabel } from '../helpers/getRfqCancelActionLabel';
import { getRfqCancelPopupText } from '../helpers/getRfqCancelPopupText';

interface RfqDetailActionBarProps {
    rfq: RequestForQuoteGatewayMessage;
}

interface RfqDetailAction {
    id: string;
    label: string;
    onSelect: () => void;
    Icon?: ShortcutMenuContextAction['Icon'];
    destructive?: boolean;
}

export const RfqDetailActionBar = ({ rfq }: RfqDetailActionBarProps) => {
    const role = useRole();
    const [showDeclinePopup, setShowDeclinePopup] = useState(false);
    const [showInitialEnquiryPopup, setShowInitialEnquiryPopup] = useState(false);
    const [showReopenPopup, setShowReopenPopup] = useState(false);
    const { addErrorToast, addLoadingToast } = useToasts();
    const { downloadPdf } = useRfqExport();
    const declineRfqMutation = useRfqDecline();
    const reopenOfferMutation = useOfferReopen();

    const isMultipleOffersEnabled = rfq.multipleOfferAvailability === MultipleOfferAvailability.ENABLED;
    const isDeclinable = [OfferState.ENQUIRY, OfferState.QUOTED].includes(rfq.state);
    const openOffers = getOpenRfqOffers(rfq.offers);
    const hasQuotedOffers = openOffers.some((offer) => offer.wasQuoted());
    const initialOffer = rfq.offers[0];

    const handleDeclineSubmit = async (cancelReason: string) => {
        await declineRfqMutation.mutateAsync(
            {
                id: rfq.id,
                cancelReason,
            },
            {
                onSuccess: () => {
                    setShowDeclinePopup(false);
                },
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    const handleReopenSubmit = async () => {
        if (!initialOffer?.id) {
            return;
        }

        await reopenOfferMutation.mutateAsync(
            { offerId: initialOffer.id, requestForQuoteId: rfq.id },
            {
                onSuccess: () => {
                    setShowReopenPopup(false);
                },
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    const actions: RfqDetailAction[] = [];

    if (initialOffer?.id) {
        actions.push({
            id: 'initial-order',
            label: translate('offer.detail.header.actions.initialOrder'),
            Icon: IconExport,
            onSelect: () => setShowInitialEnquiryPopup(true),
        });
    }

    actions.push({
        id: 'export-rfq-pdf',
        label: translate('rfq.detail.actions.export.pdf'),
        Icon: IconDownload,
        onSelect: () =>
            addLoadingToast(
                () => downloadPdf(createRfqExportRequest(rfq)),
                translate('offer.detail.header.actions.export.downloadingPdf'),
                translate('offer.detail.header.actions.export.success'),
                translate('global.genericToastError')
            ),
    });

    if (role.isSupplier() && role.hasWriteRights(rfq.supplierId) && isDeclinable && openOffers.length > 0) {
        actions.push({
            id: 'decline-rfq',
            label: getRfqCancelActionLabel({
                hasQuotedOffers,
                isMultipleOffersEnabled,
                offerCount: openOffers.length,
            }),
            Icon: IconCancel,
            destructive: true,
            onSelect: () => setShowDeclinePopup(true),
        });
    }

    if (role.isSuperAdmin() && initialOffer && isAdminReopenableOffer(initialOffer)) {
        actions.push({
            id: 'reopen-enquiry',
            label: translate('offer.detail.header.actions.reopenEnquiry'),
            Icon: IconRestore,
            onSelect: () => setShowReopenPopup(true),
        });
    }

    useRegisterShortcutMenuContextActions(
        actions.map((action) => ({
            id: action.id,
            label: action.label,
            onSelect: action.onSelect,
            Icon: action.Icon,
            destructive: action.destructive,
        }))
    );

    return (
        <>
            <DropdownMenu
                options={actions.map((action) => ({
                    label: action.label,
                    onSelect: action.onSelect,
                    destructive: action.destructive,
                }))}
                align="end"
                trigger={<IconButton emphasis="medium" Icon={IconDots} />}
            />

            {showDeclinePopup ? (
                <CancelPopup
                    isOpen={showDeclinePopup}
                    onSubmit={({ cancelReason }) => handleDeclineSubmit(cancelReason)}
                    onDismiss={() => setShowDeclinePopup(false)}
                    isSubmitting={declineRfqMutation.isPending}
                    requireReason={false}
                    text={getRfqCancelPopupText({
                        hasQuotedOffers,
                        isMultipleOffersEnabled,
                        offerCount: openOffers.length,
                    })}
                />
            ) : null}

            {showInitialEnquiryPopup && initialOffer?.id ? (
                <InitialEnquiryPopup
                    offerId={initialOffer.id}
                    onDismiss={() => setShowInitialEnquiryPopup(false)}
                    isOpen={showInitialEnquiryPopup}
                />
            ) : null}

            {showReopenPopup ? (
                <SimpleConfirmPopup
                    isOpen={showReopenPopup}
                    onSubmit={handleReopenSubmit}
                    onDismiss={() => setShowReopenPopup(false)}
                    isSubmitting={reopenOfferMutation.isPending}
                    headline={translate('offer.detail.header.actions.reopenEnquiryPopup.headline')}
                    description={translate('offer.detail.header.actions.reopenEnquiryPopup.description')}
                    buttonText={translate('offer.detail.header.actions.reopenEnquiryPopup.button')}
                />
            ) : null}
        </>
    );
};
